import { css } from '@emotion/react';
import * as React from 'react';
import { Link } from 'react-router';

import ICallLog from '../../../interfaces/ICallLog';
import { IUserWithRole, UserRoleCategory } from '../../../interfaces/IUser';
import guidedSellingExperienceNavigation from '../../../pages/GuidedSellingExperience/navigation';
import authInfo from '../../../services/authInfo';
import colors from '../../../theme/colors';
import { spacings } from '../../../theme/variables';
import FlexBox from '../FlexBox';
import { ChevronLeftDouble, ChevronRightDouble, PhoneIcon, PhoneMissedIcon, TickIcon, TransferIcon } from '../icons';
import Text from '../Text';

const CallIndicator = ({
  currentCallLog,
  title,
  type,
  isFakeLead,
  action,
  isConference,
  canAssignToAgent,
  collapsed,
  setCollapsed
}: {
  currentCallLog: ICallLog;
  title: string;
  type: string;
  isFakeLead: boolean;
  action?: React.JSX.Element;
  participants?: IUserWithRole[];
  isConference?: boolean;
  canAssignToAgent?: boolean;
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
}): React.JSX.Element => {
  const Icon = (() => {
    if (currentCallLog.is_call_ended) {
      return PhoneMissedIcon;
    }
    if (!canAssignToAgent) {
      return TickIcon;
    }
    if (isConference) {
      return TransferIcon;
    }
    return PhoneIcon;
  })();

  const navigationLink = (() => {
    if (authInfo.roleCategory === UserRoleCategory.CSR && currentCallLog.person_gid) {
      return `/people/${currentCallLog.person_gid}`;
    }
    return guidedSellingExperienceNavigation.forCall({ call_log_id: currentCallLog.id });
  })();

  return (
    <>
      {collapsed ? (
        <div
          data-testid="call-indicator"
          css={css`
            background: ${colors.neutral600};
            opacity: 0.6;
            border-radius: 8px 0px 0px 8px;
            box-sizing: border-box;
            box-shadow: 4px 4px 4px hsl(0deg 0% 0% / 20%);
            cursor: grab;
            width: 50px;
            height: 74px;

            border-left: 4px solid ${colors.neutral600};
            border-top: 4px solid ${colors.neutral600};
            border-bottom: 4px solid ${colors.neutral600};

            .expand-button {
              visibility: hidden;
            }

            :hover {
              border-top: 4px solid ${colors.aqua};
              border-bottom: 4px solid ${colors.aqua};
              border-left: 4px solid ${colors.aqua};
              opacity: 1;
            }

            :hover .expand-button {
              visibility: visible;
            }
          `}
        >
          <FlexBox fitParentHeight mt={spacings.px4} columnDirection alignItemsCenter>
            <ChevronLeftDouble
              className="expand-button"
              width={20}
              height={20}
              color={colors.aqua}
              css={css`
                cursor: pointer;
              `}
              onClick={() => setCollapsed(false)}
            />
            <Icon width={18} height={18} color={currentCallLog.is_call_ended ? colors.statusRed : colors.aqua} />
          </FlexBox>
        </div>
      ) : (
        <div
          data-testid="call-indicator"
          css={css`
            background: ${colors.neutral600};
            border-radius: 8px;
            width: 420px;
            display: flex;
            flex-direction: column;
            box-shadow: 4px 4px 4px hsl(0deg 0% 0% / 20%);
            cursor: grab;
          `}
        >
          <FlexBox
            justifySpaceBetween
            mv={16}
            mh={16}
            customCss={css`
              height: 56px;
            `}
          >
            <FlexBox
              customCss={css`
                display: flex;
                flex: 1 1 0%;
                align-items: center;
                min-width: 0px;
              `}
            >
              <FlexBox alignItemsCenter pr={12}>
                <Icon height={16} width={16} color={currentCallLog.is_call_ended ? colors.statusRed : colors.aqua} />
              </FlexBox>

              <FlexBox columnDirection>
                <FlexBox
                  justifySpaceBetween
                  alignItemsCenter
                  customCss={css`
                    margin-bottom: 10px;
                  `}
                >
                  <Text
                    color={colors.white}
                    customCss={css`
                      a {
                        position: relative;
                        color: ${colors.aqua};
                      }
                      a::after {
                        position: absolute;
                        bottom: -2px;
                        left: 0;
                        width: 100%;
                        height: 1px;
                        background-color: ${colors.aqua};
                        content: '';
                      }
                    `}
                    bold
                    className="fs-mask"
                  >
                    {isFakeLead ? (
                      title
                    ) : (
                      <Link data-testid="call-indicator-link" to={navigationLink}>
                        {title}
                      </Link>
                    )}
                  </Text>
                  <FlexBox ml={6}>{action}</FlexBox>
                </FlexBox>
                <Text type="small" color={colors.white}>
                  {type}
                </Text>
              </FlexBox>
            </FlexBox>
            <FlexBox
              alignItemsCenter
              customCss={css`
                cursor: pointer;
              `}
              onClick={() => setCollapsed(true)}
            >
              <ChevronRightDouble color={colors.aqua} />
              <Text ml={spacings.px8} color={colors.aqua}>
                Hide
              </Text>
            </FlexBox>
          </FlexBox>
        </div>
      )}
    </>
  );
};

export default CallIndicator;
